import React, { useCallback, useMemo, useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { GenericButton } from "@ui-components/Button/GenericButton";
import MultiFilterDropdown, {
	type MultiFilterFieldConfig,
	type MultiFilterValue,
} from "@ui-components/Filter/MultiFilterDropdown";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { type ParsedFilter } from "kontrolEdge/helpers/advancedFilters";
import { useSubscriptions } from "./hooks";
import {
	buildSubscriptionColumns,
	getStateBadge,
	SERVICE_TYPE_LABEL,
	type SortState,
} from "./SubscriptionsData";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import RenewSubscriptionModal from "./RenewSubscriptionModal";
import SubscriptionHistoryModal from "./SubscriptionHistoryModal";
import type {
	Subscription,
	SubscriptionListQuery,
	SubscriptionServiceType,
	SubscriptionSortKey,
	SubscriptionState,
} from "./types";

/**
 * Account → Subscriptions (CTX-26).
 *
 * Administers the internal subscription terms that replaced Chargebee: which
 * services are covered, until when, how many times they have been renewed,
 * and renewing one from here.
 *
 * Visibility and access are NOT decided in this component. The section is
 * gated upstream by `isSubscriptionsManagerEnabled()` (does this build ship
 * it) and `getSubscriptionsAllowedTenantTypes()` (who may use it), applied by
 * both the sidemenu and the route guard so the two cannot drift.
 */

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 30, 50];

/** Attribute names offered by the searchbar's suggestion list. */
const SEARCH_SUGGESTED_KEYS = ["Subscription ID", "Service", "Customer", "Type"];

/** Filter field ids — also the keys of the `MultiFilterValue` map. */
const FILTER_FIELD = {
	type: "serviceType",
	service: "serviceName",
	state: "state",
	customer: "tenantId",
} as const;

/** Every lifecycle state, in the order an operator reasons about them. */
const FILTERABLE_STATES: SubscriptionState[] = [
	"active",
	"future",
	"expiring",
	"expired",
	"renewed",
	"suspended",
	"cancelled",
	"reactivated",
];

/**
 * States the table opens on (CTX-523). Asked for directly: opening the view
 * answers "what is active", and nothing else.
 *
 * What it leaves out is what made the view confusing — a renewal creates a new
 * subscription and the old one stays as a record, so the same service showed up
 * twice with contradicting statuses. That history is not lost: the Status
 * filter reaches it, and every row keeps its History button.
 *
 * `reactivated` is here so the rows match the ACTIVE card, which counts both
 * (see `summarize` in the backend's list helper) — the card and the table would
 * otherwise disagree the moment a suspended subscription comes back.
 *
 * One consequence worth knowing before widening this list again: the EXPIRING
 * IN 90 DAYS card counts over the filtered set, so it reads 0 on open even when
 * subscriptions are inside the window. They are one filter change away, under
 * `expiring`.
 */
export const DEFAULT_STATES: SubscriptionState[] = ["active", "reactivated"];

/** The selection the table opens on, and the one "Reset filters" restores. */
export const DEFAULT_FILTER_VALUE: MultiFilterValue = {
	[FILTER_FIELD.state]: DEFAULT_STATES,
};

const FILTERS_STORAGE_KEY = "ctx.subscriptions.filters.v1";

const KNOWN_FILTER_FIELDS = new Set<string>(Object.values(FILTER_FIELD));
const KNOWN_STATES = new Set<string>(FILTERABLE_STATES);

/**
 * Coerce whatever is in storage into a filter value, dropping anything this
 * build no longer understands. Exported for tests.
 *
 * Returns null when the payload is not a filter object at all, which the caller
 * treats as "nothing stored" — distinct from an EMPTY object, which is a real
 * selection meaning "no filters", and has to survive a reload or clearing the
 * status filter would silently undo itself.
 *
 * States are checked against the known list because a value written by an older
 * build would otherwise be forwarded to the API as a filter nothing matches;
 * service names and customers cannot be checked here, since their valid values
 * come from the response facets.
 */
export function sanitizeStoredFilters(raw: unknown): MultiFilterValue | null {
	if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return null;

	const out: MultiFilterValue = {};
	for (const [field, ids] of Object.entries(raw as Record<string, unknown>)) {
		if (!KNOWN_FILTER_FIELDS.has(field) || !Array.isArray(ids)) continue;
		const values = ids.filter(
			(id): id is string =>
				typeof id === "string" &&
				(field !== FILTER_FIELD.state || KNOWN_STATES.has(id))
		);
		if (values.length) out[field] = values;
	}
	return out;
}

function readStoredFilters(): MultiFilterValue {
	const fallback = DEFAULT_FILTER_VALUE;
	try {
		const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
		if (raw === null) return fallback;
		return sanitizeStoredFilters(JSON.parse(raw)) ?? fallback;
	} catch {
		// Unreadable storage or malformed JSON: open on the default rather than
		// on an empty table nobody asked for.
		return fallback;
	}
}

function writeStoredFilters(value: MultiFilterValue): void {
	try {
		localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(value));
	} catch {
		/* ignore storage errors */
	}
}

const SERVICE_TYPE_OPTIONS = (
	Object.keys(SERVICE_TYPE_LABEL) as SubscriptionServiceType[]
).map((value) => ({ id: value, label: SERVICE_TYPE_LABEL[value] }));

/** One KPI in the summary card — same visual language as Tags Manager. */
const Stat: React.FC<{
	label: string;
	value: number | string;
	accent?: "default" | "warning" | "success" | "danger";
	hint?: string;
}> = ({ label, value, accent = "default", hint }) => {
	const valueColor =
		accent === "warning"
			? "text-semantic-text-warning"
			: accent === "danger"
			? "text-semantic-text-danger"
			: accent === "success"
			? "text-semantic-text-success"
			: "text-semantic-text-brand";
	return (
		<div className="flex flex-col min-w-0">
			<span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
				{label}
			</span>
			<span className={`text-2xl font-semibold mt-1 tabular-nums ${valueColor}`}>
				{value}
			</span>
			{hint && (
				<span className="text-xs text-semantic-text-secondary mt-1 truncate">
					{hint}
				</span>
			)}
		</div>
	);
};

/**
 * Collapse the searchbar's parsed filters into the single free-text term the
 * list endpoint accepts. The searchbar can emit `key: value` pairs, but the
 * API searches id / service / customer at once, so only the values matter.
 * Joining them keeps the control usable without pretending we support
 * per-attribute querying that isn't built.
 */
function toSearchTerm(filters: ParsedFilter[]): string {
	return filters
		.map((filter) => filter.value)
		.filter(Boolean)
		.join(" ")
		.trim();
}

const SubscriptionsPage: React.FC = () => {
	const [searchFilters, setSearchFilters] = useState<ParsedFilter[]>([]);
	// Lazy initialiser: storage is read once on mount, not on every render.
	const [filterValue, setFilterValue] =
		useState<MultiFilterValue>(readStoredFilters);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	// Ordered by state so the default view reads as two blocks, `active` first
	// and `reactivated` under it, rather than interleaving them by date. The
	// backend orders on the column itself, so this rests on `active` sorting
	// before `reactivated` alphabetically — true for these two, and arbitrary
	// for any state the operator adds to the filter afterwards.
	//
	// Sorting is a single ORDER BY with no tiebreaker, so rows sharing a state
	// come back in whatever order the database picks. Clicking a column header
	// still sorts the whole table the usual way.
	const [sort, setSort] = useState<SortState>({
		sortBy: "state",
		sortDir: "asc",
	});

	const [creating, setCreating] = useState(false);
	const [renewTarget, setRenewTarget] = useState<Subscription | null>(null);
	const [historyTarget, setHistoryTarget] = useState<Subscription | null>(null);

	const search = useMemo(() => toSearchTerm(searchFilters), [searchFilters]);

	const query = useMemo<SubscriptionListQuery>(() => {
		const serviceTypes = (filterValue[FILTER_FIELD.type] ??
			[]) as SubscriptionServiceType[];
		const serviceNames = filterValue[FILTER_FIELD.service] ?? [];
		const states = (filterValue[FILTER_FIELD.state] ?? []) as SubscriptionState[];
		const tenantIds = (filterValue[FILTER_FIELD.customer] ?? []).map(Number);
		return {
			page,
			pageSize,
			search: search || undefined,
			serviceTypes: serviceTypes.length ? serviceTypes : undefined,
			serviceNames: serviceNames.length ? serviceNames : undefined,
			states: states.length ? states : undefined,
			tenantIds: tenantIds.length ? tenantIds : undefined,
			sortBy: sort.sortBy,
			sortDir: sort.sortDir,
		};
	}, [page, pageSize, search, filterValue, sort]);

	const { data, loading, error, hasFetched, refresh } = useSubscriptions(query);

	// Anchored once per mount so every row's "expiring soon" accent and the
	// renewal preview agree on what "today" is, even across a long session.
	const now = useMemo(() => new Date(), []);

	const handleSearchChange = useCallback((filters: ParsedFilter[]) => {
		setSearchFilters(filters);
		setPage(1);
	}, []);

	const handleFilterApply = useCallback((next: MultiFilterValue) => {
		setFilterValue(next);
		writeStoredFilters(next);
		setPage(1);
	}, []);

	const handleSort = useCallback((key: SubscriptionSortKey) => {
		setSort((prev) => ({
			sortBy: key,
			sortDir: prev.sortBy === key && prev.sortDir === "asc" ? "desc" : "asc",
		}));
		setPage(1);
	}, []);

	const columns = useMemo(
		() =>
			buildSubscriptionColumns({
				now,
				sort,
				onSort: handleSort,
				onRenew: setRenewTarget,
				onViewHistory: setHistoryTarget,
			}),
		[now, sort, handleSort]
	);

	// Service names and customers are data-dependent, so they come from the
	// response facets rather than being derived from the page in hand — the
	// picker must offer values that aren't on the current page.
	const filterFields = useMemo<MultiFilterFieldConfig[]>(
		() => [
			{
				id: FILTER_FIELD.type,
				label: "Service type",
				options: SERVICE_TYPE_OPTIONS,
			},
			{
				id: FILTER_FIELD.service,
				label: "Service",
				options: (data?.facets.serviceNames ?? []).map((name) => ({
					id: name,
					label: name,
				})),
				searchPlaceholder: "Find a service",
				emptyOptionsMessage: "No services to filter by yet",
			},
			{
				id: FILTER_FIELD.state,
				label: "Status",
				options: FILTERABLE_STATES.map((state) => ({
					id: state,
					label: getStateBadge(state).label,
				})),
			},
			{
				id: FILTER_FIELD.customer,
				label: "Customer",
				options: (data?.facets.customers ?? []).map((customer) => ({
					id: String(customer.tenantId),
					label: customer.name,
				})),
				searchPlaceholder: "Find a customer",
				emptyOptionsMessage: "No customers to filter by yet",
			},
		],
		[data?.facets]
	);

	const rows = data?.items ?? [];
	const summary = data?.summary;
	const total = data?.total ?? 0;

	// A first-load failure has nothing to fall back to, so the page is replaced
	// by the error state. Later failures keep the stale rows and show a banner
	// instead — see below.
	if (error && !hasFetched) {
		return (
			<div className="p-6 space-y-4">
				<ErrorFetchingItems text={error} />
				<div className="flex justify-center">
					<GenericButton
						label="Retry"
						variant="secondary"
						size="md"
						onClick={() => {
							void refresh();
						}}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-6">
			<header className="flex flex-wrap items-start gap-3">
				<div className="flex-1 min-w-[260px]">
					<h1 className="text-h3 font-bold">Subscriptions</h1>
					<p className="text-semantic-text-secondary mt-1 max-w-[66ch]">
						Internal subscription terms per service: coverage, renewals and history by
						customer.
					</p>
				</div>
			</header>

			<GenericCard
				isCollapsible={false}
				variant="primary"
				bodyContent={
					<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
						<Stat
							label="Subscriptions"
							value={summary?.total ?? 0}
							hint="Matching current filters"
						/>
						<Stat
							label="Active"
							value={summary?.active ?? 0}
							accent="success"
							hint="Currently covered"
						/>
						<Stat
							label="Expiring in 90 days"
							value={summary?.expiringSoon ?? 0}
							accent="warning"
							hint="Within the notification window"
						/>
						<Stat
							label="Expired"
							value={summary?.expired ?? 0}
							accent={summary?.expired ? "danger" : "success"}
							hint={summary?.expired ? "Never renewed" : "Nothing lapsed"}
						/>
					</div>
				}
			/>

			<GenericCard
				isCollapsible={false}
				className="flex flex-col"
				bodyContent={
					<div className="flex flex-col">
						<div className="flex flex-col gap-3 lg:flex-row lg:items-start my-3 px-1 sm:px-0">
							<div className="w-full lg:max-w-[420px] min-w-0">
								<GenericSearchbar
									placeholder="Find by attribute or value"
									suggestedKeys={SEARCH_SUGGESTED_KEYS}
									onFiltersChange={handleSearchChange}
									liveFiltering
									debounceMs={250}
								/>
							</div>
							<div className="w-full lg:max-w-[520px] min-w-0">
								<MultiFilterDropdown
									fields={filterFields}
									value={filterValue}
									onApply={handleFilterApply}
									// Clearing restores the default rather than
									// stripping it: this view opens on a filter, and
									// re-selecting the four states by hand is not a
									// reasonable way back to it.
									resetValue={DEFAULT_FILTER_VALUE}
									clearLabel="Reset filters"
									triggerLabel="Filters"
									cardDescription="Combined with AND across fields, OR within a field."
								/>
							</div>
							<div className="flex lg:ml-auto shrink-0">
								<GenericButton
									label="New subscription"
									variant="brand"
									size="md"
									onClick={() => setCreating(true)}
								/>
							</div>
						</div>

						{/* A failure after the first successful load keeps the stale rows on
						    screen rather than blanking the table; the banner explains why
						    they may be out of date. */}
						{error && hasFetched && (
							<p className="mb-3 rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-2 text-sm text-semantic-text-danger">
								{error}
							</p>
						)}

						<GenericTable<Subscription>
							columns={columns}
							data={rows}
							loading={loading}
							width="100%"
							tableClassName="bg-semantic-bg-primary"
							maxTableBodyHeight="100%"
							rowKey={(row) => row.id}
							serverSide
							totalCount={total}
							currentPage={page}
							rowsPerPage={pageSize}
							rowsPerPageOptions={PAGE_SIZE_OPTIONS}
							onPageChange={setPage}
							onRowsPerPageChange={setPageSize}
						/>
					</div>
				}
			/>

			<CreateSubscriptionModal
				isOpen={creating}
				onClose={() => setCreating(false)}
				onCreated={() => {
					void refresh();
				}}
			/>

			<RenewSubscriptionModal
				subscription={renewTarget}
				onClose={() => setRenewTarget(null)}
				onRenewed={() => {
					void refresh();
				}}
			/>

			<SubscriptionHistoryModal
				subscription={historyTarget}
				onClose={() => setHistoryTarget(null)}
			/>
		</div>
	);
};

export default SubscriptionsPage;
